import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, type Page } from '../lib/api';
import { ErrorNotice } from '../ui/feedback';
import { DataTable, PageHead, Pager, useKeysetPage } from '../ui/table';
import { Num, Time } from '../ui/values';
import { FilterSearch, useUrlFilter } from './common';
import { paths } from './paths';
import type { AdminTeamRow } from './types';

/** Teamliste (design.md §6): Name · Besitzer · Mitglieder · Kisten · erstellt. */
export function TeamsPage() {
  const page = useKeysetPage();
  const [q, setQ] = useUrlFilter('q');
  const query = { q, cursor: page.cursor, limit: page.limit };
  const list = useQuery({
    queryKey: ['admin', 'teams', query],
    queryFn: () => api.get<Page<AdminTeamRow>>('/admin/teams', query),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="stack">
      <PageHead title="Teams" />
      <div className="filters">
        <FilterSearch label="Team suchen" value={q} onChange={setQ} />
      </div>
      {list.isError && <ErrorNotice error={list.error} onRetry={() => list.refetch()} />}
      <DataTable
        label="Teams"
        rows={list.data?.items}
        loading={list.isPending}
        rowKey={(t) => t.id}
        rowHref={(t) => paths.team(t.id)}
        empty={q ? 'Keine Teams für diese Suche.' : 'Noch keine Teams.'}
        columns={[
          { key: 'name', header: 'Name', cell: (t) => t.name },
          { key: 'owner', header: 'Besitzer', cell: (t) => t.owner.name ?? <span className="muted">–</span> },
          { key: 'members', header: 'Mitglieder', num: true, cell: (t) => <Num value={t.memberCount} /> },
          { key: 'chests', header: 'Kisten', num: true, hide: 'sm', cell: (t) => <Num value={t.chestCount} /> },
          { key: 'created', header: 'erstellt', hide: 'sm', cell: (t) => <Time iso={t.createdAt} /> },
        ]}
      />
      <Pager page={page} count={list.data?.items.length ?? 0} nextCursor={list.data?.nextCursor} />
    </div>
  );
}
